import { z } from "zod";
import { languageSchema } from "../languages";
import { id, requiredText } from "../schemas/common";

/*
 * What labels are printed on (ADR 0018 › Printing): a roll (endless) or a sheet,
 * a built-in preset or a garage's own saved medium. All lengths are millimetres.
 */

/** An endless label roll: its width and the width the printer can print on. */
export interface RollMedium {
  kind: "roll";
  width: number;
  printable: number;
}

/** A label sheet's grid: one label centred in each position. */
export interface SheetTemplate {
  columns: number;
  rows: number;
  labelWidth: number;
  labelHeight: number;
  /** From one label's left or top edge to the next one's. */
  pitchX: number;
  pitchY: number;
  /** The first label's left and top edge on the page. */
  offsetX: number;
  offsetY: number;
}

/** A page: a free grid within its margins, or a label sheet's template. */
export interface SheetMedium {
  kind: "sheet";
  width: number;
  height: number;
  margins: { top: number; right: number; bottom: number; left: number };
  template: SheetTemplate | null;
}

export type LabelMedium = RollMedium | SheetMedium;

/** A printer's dots at 300 dpi as millimetres. */
const dots = (count: number) => Math.round(((count / 300) * 25.4 + Number.EPSILON) * 100) / 100;

const PAGE_MARGINS = { top: 10, right: 10, bottom: 10, left: 10 };

/**
 * The built-in media. The Brother rolls' printable widths are Brother's printable dots at 300 dpi
 * for continuous DK tape (QL raster command reference, as brother_ql's label table lists them:
 * 306, 413, 554, 696 and 1164 dots).
 */
export const MEDIA_PRESETS = [
  { id: "brother-62", name: "Brother 62 mm", medium: roll(62, dots(696)) },
  { id: "brother-29", name: "Brother 29 mm", medium: roll(29, dots(306)) },
  { id: "brother-38", name: "Brother 38 mm", medium: roll(38, dots(413)) },
  { id: "brother-50", name: "Brother 50 mm", medium: roll(50, dots(554)) },
  { id: "brother-102", name: "Brother 102 mm", medium: roll(102, dots(1164)) },
  { id: "a4", name: "A4", medium: sheet(210, 297) },
  { id: "letter", name: "Letter", medium: sheet(215.9, 279.4) },
] as const satisfies readonly { id: string; name: string; medium: LabelMedium }[];

export type MediaPresetId = (typeof MEDIA_PRESETS)[number]["id"];

function roll(width: number, printable: number): RollMedium {
  return { kind: "roll", width, printable };
}

function sheet(width: number, height: number): SheetMedium {
  return { kind: "sheet", width, height, margins: PAGE_MARGINS, template: null };
}

const length = (max: number) =>
  z
    .number({ error: "validation.number" })
    .positive({ error: "validation.positive" })
    .max(max, { error: "validation.tooLarge" });
const margin = z
  .number({ error: "validation.number" })
  .min(0, { error: "validation.positive" })
  .max(100, { error: "validation.tooLarge" });

/** A garage's own medium, as saved from the print page (`label_media.dimensions`). */
export const labelMedium = z.discriminatedUnion("kind", [
  z
    .object({ kind: z.literal("roll"), width: length(300), printable: length(300) })
    .refine((r) => r.printable <= r.width, { error: "validation.tooLarge", path: ["printable"] }),
  z.object({
    kind: z.literal("sheet"),
    width: length(1000),
    height: length(1000),
    margins: z.object({ top: margin, right: margin, bottom: margin, left: margin }),
    template: z
      .object({
        columns: z.int().positive().max(50),
        rows: z.int().positive().max(100),
        labelWidth: length(500),
        labelHeight: length(500),
        pitchX: length(500),
        pitchY: length(500),
        offsetX: margin,
        offsetY: margin,
      })
      .nullable(),
  }),
]) satisfies z.ZodType<LabelMedium>;

/** Where the print page's medium comes from: a preset or a saved medium (`label_print_setup`). */
export const mediaRef = z.union([
  z.templateLiteral(["preset:", z.enum(MEDIA_PRESETS.map((p) => p.id))]),
  z.templateLiteral(["custom:", z.string().min(1)]),
]);

/** The print options; the garage's last ones are saved with each print (`label_print_setup`). */
export const labelPrintOptions = z.object({
  /** The printer's resolution: the PNG's pixels per inch and what a module is made of. */
  dpi: z.union([z.literal(300), z.literal(600)]).default(300),
  /** QR size: 3 dots per module (7.37 mm at 300 dpi) or 4 (9.83 mm). */
  dotsPerModule: z.union([z.literal(3), z.literal(4)]).default(3),
  /** Between neighbours, across and down: the quiet zone around each QR. */
  gap: z.number().min(1).max(20).default(2),
  showCode: z.boolean().default(true),
  /** A second line with the part's primary number; the cell gets about 12 mm wide. */
  showPartNumber: z.boolean().default(false),
  /** Also download a pick list: position, code, part, location. */
  pickList: z.boolean().default(false),
  /** PNG of a roll only as wide as the printable area, for software that adds the margins. */
  printableWidthOnly: z.boolean().default(false),
  /** A wider cell than the QR and its text need; null is the narrowest that fits. */
  cellWidth: z.number().min(5).max(100).nullable().default(null),
  /** The code's size in points; null follows the QR size (5 pt at 3 dots, 6.5 pt at 4). */
  fontSize: z.number().min(3).max(20).nullable().default(null),
});
export type LabelPrintOptions = z.output<typeof labelPrintOptions>;

/** A medium saved from the print page ("Custom"): its name and its dimensions. */
export const labelMediumInput = z.object({ name: requiredText, medium: labelMedium });
export const labelMediumRename = z.object({ id, name: requiredText });

/** A print of the label basket (`POST /api/g/<garage>/labels/print`). */
export const labelPrintRequest = z.object({
  media: mediaRef,
  options: labelPrintOptions,
  format: z.enum(["pdf", "png"]),
  /** The labels, or the pick list that says which sticker goes where (always a PDF). */
  output: z.enum(["labels", "pickList"]).default("labels"),
  /** Positions of a part-used sheet to leave empty; never saved. */
  skip: z.int().min(0).max(1000).default(0),
  /** The pick list's language: the one of whoever prints. */
  language: languageSchema.default("en"),
});
export type LabelPrintRequest = z.output<typeof labelPrintRequest>;
